// src/helpers/notifications/templates/events.ts

import { TemplateMeta } from '../notificationConfig';
import { formatReadableDate } from 'helpers/date';

// turn "due_date" → "Due date"
function humanizeField(field: string) {
    const s = field.replace(/_/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
}

// detect ISO timestamp strings (roughly)
function isIsoDateString(val: any): val is string {
    return (
        typeof val === 'string' &&
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val) &&
        !isNaN(Date.parse(val))
    );
}
export const eventsTemplates: TemplateMeta[] = [
    {
        key: 'meeting.scheduled',
        label: 'Meeting Scheduled',
        type: 'events',
        icon: 'ri-calendar-event-line text-success',
        title: d => `You’ve got a meeting with ${d.invitee_name}`,
        subtitle: d => `Starts on ${formatReadableDate(d.start_time)}`,
    },
    {
        key: 'meeting.reminder',
        label: 'Meeting Reminder',
        type: 'events',
        icon: 'ri-calendar-event-line text-info',
        title: d => `Reminder: meeting with ${d.invitee_name}`,
        subtitle: d => `In ${d.minutes_before} min`,
        supportsReminder: true,
    },
    {
        key: 'meeting.reminder.digest',
        label: 'Meeting Reminders',
        type: 'events',
        icon: 'ri-calendar-event-line text-info',
        title: d => `You’ve got ${d.items.length} upcoming meetings`,
        subtitle: d =>
            d.items
                .map((i: any) => `${i.invitee_name} @ ${formatReadableDate(i.start_time)}`)
                .join(' · '),
    },
    {
        key: 'meeting.updated',
        label: 'Meeting Updated',
        type: 'events',
        icon: 'ri-calendar-event-line text-warning',
        title: d => `Meeting updated: ${d.meeting_title}`,
        subtitle: d => {
            if (!Array.isArray(d.changes) || d.changes.length === 0) {
                return 'Meeting details updated';
            }
            return d.changes
                .map((c: any) => {
                    switch (c.field) {
                        case 'start_time':
                            return `Starts → ${formatReadableDate(c.new_value)}`;
                        case 'end_time':
                            return `Ends → ${formatReadableDate(c.new_value)}`;
                        case 'invitee_name':
                            return `With → ${c.new_value}`;
                        case 'location':
                            return `Location → ${c.new_value}`;
                        case 'status':
                            return `Status → ${c.new_value}`;
                        default:
                            const label = humanizeField(c.field);
                            if (isIsoDateString(c.new_value)) {
                                return `${label} → ${formatReadableDate(c.new_value)}`;
                            }
                            return `${label} → ${c.new_value}`;
                    }
                })
                .join(' · ');
        },
    },
    {
        key: 'meeting.cancelled',
        label: 'Meeting Cancelled',
        type: 'events',
        icon: 'ri-calendar-event-line text-danger',
        title: d => `Meeting cancelled: ${d.meeting_title}`,
        subtitle: d => `By ${d.cancelled_by}`,
    },
    {
        key: 'task.assigned',
        label: 'Task Assigned',
        type: 'events',
        icon: 'ri-task-line text-success',
        title: d => `New task: ${d.task_title}`,
        subtitle: d => `Assigned by ${d.assigner}`,
    },
    {
        key: 'task.due',
        label: 'Task Due Soon',
        type: 'events',
        icon: 'ri-task-line text-info',
        title: d => `Task due: ${d.task_title}`,
        subtitle: d => `Deadline ${formatReadableDate(d.due_date)}`,
        supportsReminder: true,
    },
    {
        key: 'task.due.digest',
        label: `Task Reminders`,
        type: 'updates',
        icon: 'ri-task-line text-info',
        title: d => `You’ve got ${d.items.length} tasks due soon`,
        subtitle: d =>
            d.items
                .map((i: any) => `${i.task_title} (${formatReadableDate(i.due_date)})`)
                .join(' · '),
    },
    {
        key: 'task.completed',
        label: 'Task Completed',
        type: 'events',
        icon: 'ri-task-line text-success',
        title: d => `Task completed: ${d.task_title}`,
        subtitle: d => `By ${d.completed_by}`,
    },
    {
        key: 'task.updated',
        label: 'Task Updated',
        type: 'events',
        icon: 'ri-task-line text-warning',
        title: d => `Task updated: ${d.task_title}`,
        subtitle: d => {
            if (!Array.isArray(d.changes) || d.changes.length === 0) {
                return 'Task details updated';
            }
            return d.changes
                .map((c: any) => {
                    switch (c.field) {
                        case 'due_date':
                            return `Due → ${formatReadableDate(c.new_value)}`;
                        case 'assigned_to':
                            return `Assignee → ${c.new_value}`;
                        case 'status':
                            return `Status → ${c.new_value}`;
                        case 'priority':
                            return `Priority → ${c.new_value}`;
                        default:
                            const label = humanizeField(c.field);
                            if (isIsoDateString(c.new_value)) {
                                return `${label} → ${formatReadableDate(c.new_value)}`;
                            }
                            return `${label} → ${c.new_value}`;
                    }
                })
                .join(' · ');
        },
    },
    {
        key: 'task.deleted',
        label: 'Task deleted',
        type: 'events',
        icon: 'ri-task-line text-danger',
        title: d => `Task deleted: ${d.task_title}`,
        subtitle: d => `By ${d.deleted_by}`,
    },
];
